import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { equipVibeSet, fetchVibeSets } from '../_helpers/vibeSets.js';
import { loadFxLoadout } from '../_helpers/fxPackages.js';

/**
 * The vibe set picker under the queue strip (ENG-812, FX-6): the listener's
 * owned vibe sets, a live preview on the queue's own FX stage, and the pick,
 * saved to the account and switched live.
 *
 * `fx` is the page's `useFxStage` controller; `enabled` is whether the picker
 * may be used at all (signed in, effects on). Signed out there is no loadout,
 * so the stage keeps the default vibe and nothing here calls `/me`.
 *
 * - `preview(item)` puts the item's vibe on the stage now (`fx.setVibe`,
 *   which keeps the Reduce effects overrides); `endPreview()` puts back the
 *   one worn. Nothing is saved.
 * - `pick(item)` saves it (`PUT /me/skins/equipped/vibe_set`) and, after the
 *   2xx, switches the stage to it, mid-build or mid-afterglow alike. A refused
 *   pick puts back the one worn and reports `error`.
 * - `sync()` re-reads the loadout and switches to it: after a purchase, which
 *   equips what was bought.
 * - `applyWorn(fxPackageId | null)` switches to a vibe set worn on the skin
 *   shelf (null: taken off, so the default).
 *
 * Returns `{ enabled, open, status, items, equippedId, previewId, busyId,
 * error, toggle, close, retry, preview, endPreview, pick, sync, applyWorn }`, `status`
 * being 'idle' | 'loading' | 'ready' | 'error' for the list.
 */
export const useVibeSetPicker = ({ fx = null, enabled = false } = {}) => {
  const fxRef = useRef(fx);
  fxRef.current = fx;
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;
  const [open, setOpen] = useState(false);
  const [list, setList] = useState({ status: 'idle', items: [], equippedId: null, error: null });
  const [previewId, setPreviewId] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState(null);
  // The vibe package worn now, once this page has changed it (else the stage's loadout).
  const wornRef = useRef(null);
  // Whether a preview is on the stage, so ending one only restores when needed.
  const previewingRef = useRef(false);
  // The latest stage switch: an earlier one still resolving its package is dropped.
  const switchRef = useRef(0);
  // The latest list read.
  const readRef = useRef(0);
  const busyRef = useRef(false);

  /** Puts vibe package `id` on the stage, loading it first when needed. */
  const show = useCallback((id) => {
    switchRef.current += 1;
    const turn = switchRef.current;
    const stage = fxRef.current;
    if (!stage || stage.status === 'off' || !id) return Promise.resolve();
    return Promise.resolve(stage.resolvePackage(id, 'vibe'))
      .catch(() => null)
      .then((resolved) => {
        if (switchRef.current !== turn) return;
        fxRef.current?.setVibe(resolved ?? id);
      });
  }, []);

  const worn = useCallback(() => wornRef.current ?? fxRef.current?.loadout?.vibe ?? fxRef.current?.defaults?.vibe ?? null, []);

  const endPreview = useCallback(() => {
    // While a pick saves, the stage keeps showing it; a refusal puts the worn one back.
    if (!previewingRef.current || busyRef.current) return;
    previewingRef.current = false;
    setPreviewId(null);
    show(worn());
  }, [show, worn]);

  const preview = useCallback((item) => {
    if (!enabledRef.current || !item?.fxPackageId || busyRef.current) return;
    previewingRef.current = true;
    setPreviewId(item.id);
    show(item.fxPackageId);
  }, [show]);

  const load = useCallback(() => {
    readRef.current += 1;
    const read = readRef.current;
    setList((current) => ({ ...current, status: 'loading', error: null }));
    fetchVibeSets()
      .then(({ items, equippedId }) => {
        if (readRef.current === read) setList({ status: 'ready', items, equippedId, error: null });
      })
      .catch((err) => {
        if (readRef.current === read) setList((current) => ({ ...current, status: 'error', error: err ?? { status: 500 } }));
      });
  }, []);

  const close = useCallback(() => {
    setOpen(false);
    setError(null);
    endPreview();
  }, [endPreview]);

  const toggle = useCallback(() => {
    if (open) {
      close();
      return;
    }
    if (!enabledRef.current) return;
    setError(null);
    setOpen(true);
    load();
  }, [open, close, load]);

  const pick = useCallback((item) => {
    if (!enabledRef.current || !item?.id || busyRef.current) return;
    if (item.id === list.equippedId) {
      close();
      return;
    }
    busyRef.current = true;
    setBusyId(item.id);
    setError(null);
    equipVibeSet(item.id)
      .then(() => {
        wornRef.current = item.fxPackageId;
        previewingRef.current = false;
        setPreviewId(null);
        setList((current) => ({ ...current, equippedId: item.id }));
        setOpen(false);
        return show(item.fxPackageId);
      })
      .catch((err) => {
        setError(err ?? { status: 500 });
        busyRef.current = false;
        previewingRef.current = true;
        endPreview();
      })
      .finally(() => {
        busyRef.current = false;
        setBusyId(null);
      });
  }, [list.equippedId, close, show, endPreview]);

  const sync = useCallback(() => {
    const stage = fxRef.current;
    if (!enabledRef.current || !stage || stage.status === 'off') return Promise.resolve();
    return loadFxLoadout(stage.defaults ?? null).then((loadout) => {
      if (!loadout?.vibe) return undefined;
      wornRef.current = loadout.vibe;
      if (open) load();
      return previewingRef.current ? undefined : show(loadout.vibe);
    });
  }, [open, load, show]);

  /**
   * A vibe set worn or taken off elsewhere (the skin shelf), already saved:
   * switch to its package, or to the default (Classic) for a take-off.
   */
  const applyWorn = useCallback((fxPackageId) => {
    const stage = fxRef.current;
    if (!enabledRef.current || !stage) return Promise.resolve();
    const id = fxPackageId || stage.defaults?.vibe || null;
    if (!id) return Promise.resolve();
    wornRef.current = id;
    previewingRef.current = false;
    setPreviewId(null);
    return show(id);
  }, [show]);

  // Signed out or effects off: the picker closes, and a preview comes off.
  useEffect(() => {
    if (!enabled) close();
  }, [enabled, close]);

  return useMemo(() => ({
    enabled: Boolean(enabled),
    open,
    status: list.status,
    items: list.items,
    equippedId: list.equippedId,
    listError: list.error,
    previewId,
    busyId,
    error,
    toggle,
    close,
    retry: load,
    preview,
    endPreview,
    pick,
    sync,
    applyWorn,
  }), [enabled, open, list, previewId, busyId, error, toggle, close, load, preview, endPreview, pick, sync, applyWorn]);
};

export default useVibeSetPicker;
